import React, { useState, useEffect } from 'react';
import { getFinancialYears, createSession } from '../services/api';
import '../styles/CompanyYearSelection.css';

const CompanyYearSelection = ({ userData, onSelectionComplete, onLogout }) => {
  const [selectedCompany, setSelectedCompany] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [financialYears, setFinancialYears] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [yearsLoading, setYearsLoading] = useState(false);
  const [rememberSelection, setRememberSelection] = useState(true);

  const isSuperAdmin = userData.user.issuperadmin;

  useEffect(() => {
    const lastSelection = localStorage.getItem('ltms_last_selection');
    
    if (rememberSelection && lastSelection) {
      try {
        const { companyId, yearId } = JSON.parse(lastSelection);
        const hasAccess = userData.companies.some(c => c.companyid === companyId);
        
        if (hasAccess) {
          setSelectedCompany(companyId.toString());
          return;
        }
      } catch (error) {
        console.error('Error loading last selection:', error);
        localStorage.removeItem('ltms_last_selection');
      }
    }

    if (userData.companies && userData.companies.length > 0) {
      const defaultCompany = userData.companies.find(c => c.isdefault) || userData.companies[0];
      setSelectedCompany(defaultCompany.companyid.toString());
    }
  }, [userData, rememberSelection]);

  useEffect(() => {
    if (selectedCompany) {
      fetchFinancialYears(selectedCompany);
    } else {
      setFinancialYears([]);
      setSelectedYear('');
    }
  }, [selectedCompany]);

  const fetchFinancialYears = async (companyId) => {
    setYearsLoading(true);
    setError('');
    setSelectedYear('');
    
    try {
      const response = await getFinancialYears(companyId);
      
      if (response.success) {
        setFinancialYears(response.data);
        
        const lastSelection = localStorage.getItem('ltms_last_selection');
        let yearSet = false;
        
        if (rememberSelection && lastSelection) {
          try {
            const { companyId: lastCompanyId, yearId } = JSON.parse(lastSelection);
            if (lastCompanyId === parseInt(companyId)) {
              const yearExists = response.data.some(y => y.yearid === yearId);
              if (yearExists) {
                setSelectedYear(yearId.toString());
                yearSet = true;
              }
            }
          } catch (error) {
            console.error('Error loading last year selection:', error);
          }
        }
        
        if (!yearSet) {
          const currentYear = response.data.find(y => y.iscurrent);
          if (currentYear) {
            setSelectedYear(currentYear.yearid.toString());
          } else if (response.data.length > 0) {
            setSelectedYear(response.data[0].yearid.toString());
          }
        }
      } else {
        setError(response.message || 'Failed to fetch financial years');
        setFinancialYears([]);
      }
    } catch (err) {
      setError(err.message || 'An error occurred while fetching financial years');
      setFinancialYears([]);
    } finally {
      setYearsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!selectedCompany || !selectedYear) {
      setError('Please select both company and financial year');
      return;
    }

    setLoading(true);

    try {
      const response = await createSession(
        userData.user.userid,
        parseInt(selectedCompany),
        parseInt(selectedYear)
      );
      
      if (response.success) {
        if (rememberSelection) {
          localStorage.setItem('ltms_last_selection', JSON.stringify({
            companyId: parseInt(selectedCompany),
            yearId: parseInt(selectedYear)
          }));
        } else {
          localStorage.removeItem('ltms_last_selection');
        }
        
        // Transform the response to match expected format
        const sessionData = {
          user: {
            userid: response.data.session.userid,
            username: response.data.session.username,
            fullname: response.data.session.fullname,
            issuperadmin: response.data.session.issuperadmin
          },
          company: {
            companyid: response.data.session.companyid,
            companyname: response.data.session.companyname,
            companycode: response.data.session.companycode
          },
          financial_year: {
            yearid: response.data.session.yearid,
            yearname: response.data.session.yearname,
            yearcode: response.data.session.yearcode,
            startdate: response.data.session.startdate,
            enddate: response.data.session.enddate,
            iscurrent: response.data.session.iscurrent
          },
          session: response.data.session  // Keep original for compatibility
        };
        
        console.log('📦 Transformed session data:', sessionData);
        onSelectionComplete(sessionData);
      } else {
        setError(response.message || 'Failed to create session');
      }
    } catch (err) {
      setError(err.message || 'An error occurred while creating session');
    } finally {
      setLoading(false);
    }
  };

  const handleCompanyChange = (e) => {
    setSelectedCompany(e.target.value);
    setSelectedYear('');
  };

  const getCompanyDetails = () => {
    if (!selectedCompany) return null;
    return userData.companies.find(c => c.companyid === parseInt(selectedCompany));
  };

  const getYearDetails = () => {
    if (!selectedYear) return null;
    return financialYears.find(y => y.yearid === parseInt(selectedYear));
  };

  const company = getCompanyDetails();
  const year = getYearDetails();

  return (
    <div className="selection-container">
      <div className="selection-card">
        <div className="selection-header">
          {isSuperAdmin && (
            <div className="super-admin-badge">
              <span className="badge-icon">👑</span>
              <span>Super Administrator</span>
            </div>
          )}
          <h2>Select Company & Financial Year</h2>
          <p className="welcome-text">
            Welcome back, <strong>{userData.user.fullname}</strong>!
            {isSuperAdmin && <span className="role-indicator"> (Super Admin)</span>}
          </p>
          <p className="instruction-text">
            {isSuperAdmin 
              ? 'You have full system access. Select any company and financial year to continue.'
              : 'Please select your company and financial year to continue'
            }
          </p>
        </div>

        <form onSubmit={handleSubmit} className="selection-form">
          <div className="form-group">
            <label htmlFor="company">
              Company <span className="required">*</span>
            </label>
            <select
              id="company"
              value={selectedCompany}
              onChange={handleCompanyChange}
              disabled={loading || yearsLoading}
              required
              className={selectedCompany ? 'has-value' : ''}
            >
              <option value="">-- Select Company --</option>
              {userData.companies.map((company) => (
                <option key={company.companyid} value={company.companyid}>
                  {company.companyname} ({company.companycode})
                  {company.isdefault ? ' ⭐' : ''}
                </option>
              ))}
            </select>
            {userData.companies.length > 1 && (
              <small className="form-hint">
                {isSuperAdmin 
                  ? `Full access to all ${userData.companies.length} companies`
                  : `You have access to ${userData.companies.length} companies`
                }
              </small>
            )}
          </div>

          {company && (
            <div className="info-card company-card">
              <div className="info-header">
                <strong>Selected Company</strong>
              </div>
              <div className="info-body">
                <p><strong>{company.companyname}</strong></p>
                <p className="info-detail">Code: {company.companycode}</p>
                {company.isdefault && (
                  <span className="badge badge-primary">Default Company</span>
                )}
              </div>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="year">
              Financial Year <span className="required">*</span>
            </label>
            <select
              id="year"
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              disabled={loading || yearsLoading || !selectedCompany}
              required
              className={selectedYear ? 'has-value' : ''}
            >
              <option value="">
                {yearsLoading ? 'Loading...' : '-- Select Financial Year --'}
              </option>
              {financialYears.map((year) => (
                <option key={year.yearid} value={year.yearid}>
                  {year.yearname} ({year.yearcode})
                  {year.iscurrent ? ' 🟢 Current' : ''}
                </option>
              ))}
            </select>
            {yearsLoading && (
              <div className="loading-indicator">
                <div className="spinner"></div>
                <span>Loading financial years...</span>
              </div>
            )}
            {!yearsLoading && financialYears.length > 0 && (
              <small className="form-hint">
                {financialYears.length} financial year{financialYears.length > 1 ? 's' : ''} available
              </small>
            )}
          </div>

          {year && (
            <div className="info-card year-card">
              <div className="info-header">
                <strong>Selected Financial Year</strong>
              </div>
              <div className="info-body">
                <p><strong>{year.yearname}</strong></p>
                <p className="info-detail">Code: {year.yearcode}</p>
                <p className="info-detail">
                  Period: {new Date(year.startdate).toLocaleDateString()} to {new Date(year.enddate).toLocaleDateString()}
                </p>
                {year.iscurrent && (
                  <span className="badge badge-success">Current Year</span>
                )}
              </div>
            </div>
          )}

          <div className="form-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={rememberSelection}
                onChange={(e) => setRememberSelection(e.target.checked)}
                disabled={loading}
              />
              <span>Remember my selection for next time</span>
            </label>
          </div>

          {error && (
            <div className="error-message">
              <strong>⚠️ Error:</strong> {error}
            </div>
          )}

          <div className="button-group">
            <button 
              type="submit" 
              className="submit-button"
              disabled={loading || yearsLoading || !selectedCompany || !selectedYear}
            >
              {loading ? (
                <>
                  <div className="button-spinner"></div>
                  <span>Creating session...</span>
                </>
              ) : (
                <>
                  <span>Continue to Dashboard</span>
                  <span className="button-icon">→</span>
                </>
              )}
            </button>
            
            <button 
              type="button" 
              className="logout-button"
              onClick={onLogout}
              disabled={loading}
            >
              <span className="button-icon">←</span>
              <span>Logout</span>
            </button>
          </div>
        </form>

        <div className="tips-section">
          <h4>💡 Quick Tips</h4>
          <ul>
            {isSuperAdmin ? (
              <>
                <li>👑 As Super Admin, you have unrestricted access to all system features</li>
                <li>You can access all companies and manage system-wide settings</li>
                <li>Use this access responsibly to maintain system integrity</li>
              </>
            ) : (
              <>
                <li>Your default company is pre-selected (marked with ⭐)</li>
                <li>Current financial year is highlighted with 🟢</li>
                <li>You can switch companies anytime from the dashboard</li>
                <li>Enable "Remember selection" for faster access next time</li>
              </>
            )}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default CompanyYearSelection;